import React, { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { X, Menu, UserCircle, ChevronDown, LogOut, CreditCard, User, LayoutGrid, UserCog, Heart, BookOpen, Thermometer, Map } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

const getExploreFeatures = (user) => [
  {
    name: "Sauna Directory",
    description: "Discover and explore sauna and cold plunge locations.",
    icon: <LayoutGrid className="w-6 h-6 text-sauna-navy" />, path: "/directory",
    previewIcon: <Map className="w-16 h-16 text-sauna-grey mx-auto" />,
    previewText: "Discover and explore sauna and cold plunge spots across Ireland and beyond with our comprehensive directory.",
    details: [
      "Interactive location map with filters",
      "Detailed facility information & reviews",
      "Real-time availability & pricing",
      "User ratings and photo galleries",
      "Advanced search and filtering options"
    ],
    ctaText: "Visit Sauna Directory", ctaLink: "/directory"
  },

  {
    name: "Log Your Dip",
    description: "Track your sauna and cold plunge sessions.",
    icon: <BookOpen className="w-6 h-6 text-sauna-navy" />, path: "/log",
    previewIcon: <BookOpen className="w-16 h-16 text-sauna-grey mx-auto" />,
    previewText: "Track your sauna and cold plunge sessions, and monitor your wellness journey and progress.",
    details: [
      "Log sauna and cold plunge sessions",
      "Track temperature and duration",
      "Monitor wellness progress",
      "View session history and stats",
      "Set personal goals and achievements"
    ],
    ctaText: "Visit Log Your Dip", ctaLink: "/log"
  },
  {
    name: "Blog",
    description: "Read the latest articles and updates.",
    icon: <BookOpen className="w-6 h-6 text-sauna-navy" />, path: "/blog",
    previewIcon: <BookOpen className="w-16 h-16 text-sauna-grey mx-auto" />,
    previewText: "Read the latest articles, tips, and updates from SaunaDip.",
    details: ["Wellness tips", "Sauna news", "Community stories"],
    ctaText: "Visit Blog", ctaLink: "/blog"
  },
  {
    name: "Contact Us",
    description: "Get in touch with our team for support, questions, or feedback.",
    icon: <UserCircle className="w-6 h-6 text-sauna-navy" />, path: "/contact",
    previewIcon: <UserCircle className="w-16 h-16 text-sauna-grey mx-auto" />,
    previewText: "Get in touch with our team for support, questions, or feedback.",
    details: [
      "Dedicated support team",
      "Quick response times",
      "Feedback and suggestions welcome",
      "We're here to help!"
    ],
    ctaText: "Contact Us", ctaLink: "/contact"
  },
  // Only show Pricing Plans for operators
  ...(user && user.role === 'operator' ? [{
    name: "Pricing Plans",
    description: "Choose the perfect plan for your sauna and wellness journey.",
    icon: <CreditCard className="w-6 h-6 text-sauna-navy" />, path: "/pricing",
    previewIcon: <CreditCard className="w-16 h-16 text-sauna-grey mx-auto" />,
    previewText: "Choose the perfect plan for your sauna and wellness journey.",
    details: [
      "Flexible pricing tiers",
      "Feature comparisons",
      "Business plans available",
      "Custom solutions"
    ],
    ctaText: "Visit Pricing Plans", ctaLink: "/pricing"
  }] : [])
];

const Navbar: React.FC = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, getDashboardRoute } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedFeatureIdx, setSelectedFeatureIdx] = useState<number | null>(null);

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to home page after logout
      window.location.href = "/";
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Show Pricing link if not logged in or if user is operator
  const showPricingLink = !user || user.role === 'operator';

  const exploreFeatures = getExploreFeatures(user);

  return (
    <div className="bg-white border-b sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo */}
          <div className="flex items-center flex-1 min-w-0">
            <Link to="/" className="flex items-center">
              <Logo className="h-8 w-auto" />
            </Link>
          </div>

          {/* Center: Nav Links */}
          <div className="hidden md:flex flex-1 justify-center">
            <nav className="flex items-center space-x-8">
              {/* Explore Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button
                  className={cn(
                    "text-base font-medium text-gray-800 hover:text-sauna-orange transition-colors px-2 flex items-center gap-1",
                    dropdownOpen && "text-sauna-orange"
                  )}
                  onClick={() => setDropdownOpen((open) => !open)}
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                  type="button"
                >
                  Explore
                  <ChevronDown className={cn("w-4 h-4 transition-transform", dropdownOpen && "rotate-180")} />
                </button>
                {dropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50 w-[700px]"
                  >
                    <div className="bg-white border border-gray-200 rounded-xl shadow-2xl flex w-full min-h-[340px] overflow-hidden">
                      {/* Left: Features List */}
                      <div className="w-1/2 p-8 pr-4 border-r border-gray-100 flex flex-col gap-2">
                        <div className="text-center text-xl font-bold text-sauna-navy mb-6">Main Features</div>
                        {exploreFeatures.map((feature, idx) => (
                          <Link
                            to={feature.path}
                            key={feature.name}
                            className={cn(
                              "flex items-start gap-4 rounded-lg px-3 py-3 hover:bg-sauna-orange/10 transition group",
                              selectedFeatureIdx === idx && "bg-sauna-orange/10"
                            )}
                            onMouseEnter={() => setSelectedFeatureIdx(idx)}
                            onFocus={() => setSelectedFeatureIdx(idx)}
                            onMouseLeave={() => setSelectedFeatureIdx(null)}
                            tabIndex={0}
                          >
                            <span className="mt-1">{feature.icon}</span>
                            <span>
                              <div className="font-semibold text-sauna-navy group-hover:text-sauna-orange text-base">{feature.name}</div>
                              <div className="text-gray-500 text-sm leading-tight mt-0.5">{feature.description}</div>
                            </span>
                          </Link>
                        ))}
                      </div>
                      {/* Right: Feature Preview */}
                      <div className="w-1/2 flex flex-col items-center justify-center p-8">
                        {selectedFeatureIdx !== null && exploreFeatures[selectedFeatureIdx] ? (
                          <>
                            {exploreFeatures[selectedFeatureIdx].previewIcon}
                            <div className="text-center text-gray-900 font-semibold text-xl mt-4">
                              {exploreFeatures[selectedFeatureIdx].name}
                            </div>
                            <div className="text-center text-gray-500 mt-2 text-base">
                              {exploreFeatures[selectedFeatureIdx].previewText}
                            </div>
                            {exploreFeatures[selectedFeatureIdx].details && (
                              <ul className="text-left mt-4 space-y-2 text-gray-700 text-sm">
                                {exploreFeatures[selectedFeatureIdx].details.map((item, i) => (
                                  <li key={i} className="flex items-start">
                                    <span className="mr-2 text-sauna-orange">•</span>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {exploreFeatures[selectedFeatureIdx].ctaText && (
                              <Link
                                to={exploreFeatures[selectedFeatureIdx].ctaLink}
                                className="mt-6 inline-block text-sauna-orange font-semibold hover:underline"
                              >
                                {exploreFeatures[selectedFeatureIdx].ctaText} &rarr;
                              </Link>
                            )}
                          </>
                        ) : (
                          <>
                            <Map className="w-16 h-16 text-sauna-grey mx-auto" />
                            <div className="text-center text-gray-400 mt-4 text-base">Hover over a feature to explore</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Directory Link */}
              <Link
                to="/directory"
                className={cn(
                  "text-base font-medium text-gray-800 hover:text-sauna-orange transition-colors px-2",
                  location.pathname === "/directory" && "text-sauna-orange font-semibold"
                )}
              >
                Directory
              </Link>
              {/* Blog Link */}
              <Link
                to="/blog"
                className={cn(
                  "text-base font-medium text-gray-800 hover:text-sauna-orange transition-colors px-2",
                  location.pathname === "/blog" && "text-sauna-orange font-semibold"
                )}
              >
                Blog
              </Link>
              {/* About Link */}
              <Link
                to="/about"
                className={cn(
                  "text-base font-medium text-gray-800 hover:text-sauna-orange transition-colors px-2",
                  location.pathname === "/about" && "text-sauna-orange font-semibold"
                )}
              >
                About
              </Link>
              {/* Pricing Link */}
              {showPricingLink && (
                <Link
                  to="/pricing"
                  className={cn(
                    "text-base font-medium text-gray-800 hover:text-sauna-orange transition-colors px-2",
                    location.pathname === "/pricing" && "text-sauna-orange font-semibold"
                  )}
                >
                  Pricing
                </Link>
              )}
            </nav>
          </div>

          {/* Right: Auth/Profile */}
          <div className="hidden md:flex flex-1 justify-end items-center space-x-4 min-w-0">
            {user ? (
              // Authenticated user - show profile menu
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 rounded-full px-2 py-1 pr-3 h-auto text-sauna-navy hover:bg-sauna-orange/10"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-sauna-orange/20 text-sauna-orange font-bold">
                        {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium hidden sm:inline">{user.name}</span>
                    <ChevronDown className="h-4 w-4 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardRoute()} className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>

                  {/* Subscription Link: Only for operators */}
                  {user.role === 'operator' && (
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="flex items-center">
                        <CreditCard className="mr-2 h-4 w-4" />
                        <span>Subscription</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {/* Become an Operator Link: Only for guests */}
                  {user.role === 'guest' && (
                    <DropdownMenuItem asChild>
                      <Link to="/become-operator" className="flex items-center text-sauna-orange font-semibold">
                        <UserCog className="mr-2 h-4 w-4" />
                        <span>Become an Operator</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // Unauthenticated user - show login/signup
              <>
                <Link to="/login" className="text-sauna-navy hover:text-sauna-orange font-medium px-3 py-2 transition-colors">
                  Login
                </Link>
                <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-semibold px-5 py-2 rounded-lg">
                  <Link to="/choose-signup">Start for free</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Navigation Toggle Button */}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
              <Menu />
              <span className="sr-only">Open menu</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Full-screen Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-[#F5542D] z-50 md:hidden overflow-y-auto">
          <div className="p-5 h-full flex flex-col">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center" onClick={() => setMobileMenuOpen(false)}>
                <Logo className="h-8 w-auto" />
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="text-white">
                <X className="h-6 w-6" />
              </Button>
            </div>
            
            {/* Mobile Menu Items */}
            <div className="mt-10 flex-1">
              <nav className="flex flex-col space-y-4">
                {/* Explore Dropdown for Mobile */}
                <div className="flex flex-col space-y-1">
                  <span className="text-xl font-medium px-2 py-3 text-white">Explore</span>
                  {exploreFeatures.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={cn(
                        "ml-4 text-lg px-2 py-2 text-sauna-beige hover:text-white",
                        location.pathname === item.path && "text-white font-semibold"
                      )}
                      onClick={() => {
                        setDropdownOpen(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
                {/* Directory Link */}
                <Link
                  to="/directory"
                  className={cn(
                    "text-xl font-medium px-2 py-3 text-white hover:text-sauna-beige",
                    location.pathname === "/directory" && "text-white font-semibold"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Directory
                </Link>
                {/* Blog Link */}
                <Link
                  to="/blog"
                  className={cn(
                    "text-xl font-medium px-2 py-3 text-white hover:text-sauna-beige",
                    location.pathname === "/blog" && "text-white font-semibold"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                {/* About Link */}
                <Link
                  to="/about"
                  className={cn(
                    "text-xl font-medium px-2 py-3 text-white hover:text-sauna-beige",
                    location.pathname === "/about" && "text-white font-semibold"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                {/* Pricing Link (Mobile) */}
                {showPricingLink && (
                  <Link
                    to="/pricing"
                    className={cn(
                      "text-xl font-medium px-2 py-3 text-white hover:text-sauna-beige",
                      location.pathname === "/pricing" && "text-white font-semibold"
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                )}
              </nav>
            </div>
            
            {/* Auth Buttons for Mobile */}
            <div className="mt-auto pb-8 space-y-4">
              {user ? (
                // Authenticated user mobile menu
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-sauna-orange/20 rounded-lg">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-sauna-blue/20 text-sauna-blue font-bold">
                        {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-sauna-beige text-sm">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Button 
                      asChild 
                      variant="ghost" 
                      className="w-full h-12 text-white hover:bg-sauna-orange/20 text-left justify-start"
                    >
                      <Link to={getDashboardRoute()} onClick={() => setMobileMenuOpen(false)}>
                        <User className="mr-3 h-5 w-5" />
                        Dashboard
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      className="w-full h-12 text-white hover:bg-sauna-orange/20 text-left justify-start"
                    >
                      <Link to="/profile" onClick={() => setMobileMenuOpen(false)}>
                        <User className="mr-3 h-5 w-5" />
                        Profile
                      </Link>
                    </Button>

                    <Button 
                      asChild 
                      variant="ghost" 
                      className="w-full h-12 text-white hover:bg-sauna-orange/20 text-left justify-start"
                    >
                      <Link to="/subscription" onClick={() => setMobileMenuOpen(false)}>
                        <CreditCard className="mr-3 h-5 w-5" />
                        Subscription
                      </Link>
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={handleLogout}
                    variant="ghost" 
                    className="w-full h-12 text-sauna-orange hover:bg-sauna-orange/20 text-left justify-start"
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    Log out
                  </Button>
                </div>
              ) : (
                // Unauthenticated user mobile menu
                <>
                  <Button 
                    asChild 
                    className="w-full h-12 bg-sauna-orange hover:bg-sauna-orange/90 text-white text-lg"
                  >
                    <Link to="/choose-signup" onClick={() => setMobileMenuOpen(false)}>
                      Sign up
                    </Link>
                  </Button>
                  
                  <div className="text-center">
                    <span className="text-sauna-beige">Existing customer? </span>
                    <Link 
                      to="/login" 
                      className="text-sauna-blue hover:text-sauna-orange"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign in
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
